export interface ScheduleTimeComboboxOption {
  value: string;
  /** 12-hour label, e.g. `8:00 AM`. */
  label: string;
}

export interface ScheduleTimeComboboxGroup {
  value: string;
  items: ScheduleTimeComboboxOption[];
}

const SCHEDULE_TIME_PERIOD_ORDER = ["AM", "PM"] as const;

/**
 * Returns the AM/PM period from a schedule time label (`8:00 AM` → `AM`).
 */
const periodFromScheduleTimeLabel = (label: string): string => {
  const period = label.trim().split(/\s+/).at(-1)?.toUpperCase();
  return period === "AM" || period === "PM" ? period : "Other";
};

/**
 * Groups schedule time options under AM and PM headings (preserves option order within each group).
 */
export const groupScheduleTimeOptions = (
  options: ScheduleTimeComboboxOption[],
): ScheduleTimeComboboxGroup[] => {
  const groups = new Map<string, ScheduleTimeComboboxOption[]>();

  for (const option of options) {
    const groupValue = periodFromScheduleTimeLabel(option.label);
    const existing = groups.get(groupValue) ?? [];
    existing.push(option);
    groups.set(groupValue, existing);
  }

  const orderedGroupValues = [
    ...SCHEDULE_TIME_PERIOD_ORDER.filter((period) => groups.has(period)),
    ...Array.from(groups.keys()).filter(
      (key) => !SCHEDULE_TIME_PERIOD_ORDER.includes(key as "AM" | "PM"),
    ),
  ];

  return orderedGroupValues.map((value) => ({
    value,
    items: groups.get(value) ?? [],
  }));
};
